import { fmtDate } from '../lib/dates.ts';
import { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { printInvoice } from './InvoicePrint';
import { authHeaders } from '../lib/api';

/**
 * The client's own billing.
 *
 * Answers the two questions a client actually has: what do I owe,
 * and what have I already paid. Everything else about their
 * subscription lives with us, not with them.
 */
type Invoice = {
  id: string;
  number: string;
  status: string;
  period_start: string | null;
  period_end: string | null;
  issued_at: string;
  due_at: string | null;
  amount_cents: number;
  currency: string | null;
  paid_at: string | null;
};

const day = fmtDate;

const money = (cents: number, ccy = 'USD') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: ccy, maximumFractionDigits: 2,
  }).format((cents ?? 0) / 100);

const STATE: Record<string, string> = {
  paid: 'ok', due: 'warn', sent: 'warn', void: 'off',
};

export const Billing = () => {
  const [rows, setRows] = useState<Invoice[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<Invoice | null>(null);
  // Fetched once: the instructions are the same on every
  // invoice, and a client staring at an amount with no way to
  // pay it is the most avoidable support email there is.
  const [payTo, setPayTo] = useState('');

  useEffect(() => {
    fetch('/admin/invoices', {
      credentials: 'same-origin', headers: authHeaders(),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setRows)
      .catch(() => setFailed(true));

    fetch('/api/branding', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => setPayTo(b?.payTo ?? ''))
      .catch(() => undefined);
  }, []);


  /**
   * The list has enough to show a row, not enough to print a bill:
   * lines and our business details only come with the full record,
   * so it is fetched at the moment it is needed.
   */
  const doPrint = async (id: string) => {
    const r = await fetch(`/admin/invoices/${id}`, {
      credentials: 'same-origin', headers: authHeaders(),
    });
    if (!r.ok) return;
    printInvoice(await r.json());
  };

  if (failed) {
    return (
      <p className="err">
        Billing could not be loaded. Only an administrator can see it.
      </p>
    );
  }

  if (!rows) return <p className="mono">Loading</p>;

  const outstanding = rows
    .filter((i) => i.status !== 'paid' && i.status !== 'void')
    .reduce((n, i) => n + i.amount_cents, 0);

  const ccy = rows[0]?.currency ?? 'USD';

  return (
    <>
      <div className="cardRow">
        <div className="statCard">
          <span className="mono">Outstanding</span>
          <b className={outstanding ? 'owed' : undefined}>
            {money(outstanding, ccy)}
          </b>
          <span className="sub">
            {outstanding ? 'across unpaid invoices' : 'nothing due'}
          </span>
        </div>
        <div className="statCard">
          <span className="mono">Invoices</span>
          <b>{rows.length}</b>
          <span className="sub">
            {rows.filter((i) => i.status === 'paid').length} paid
          </span>
        </div>
      </div>

      {!rows.length ? (
        <p className="sub">
          No invoices yet. They appear here as they are raised.
        </p>
      ) : (
        <table className="dataTable">
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Period</th>
              <th>Issued</th>
              <th>Status</th>
              <th className="r">Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((i) => (
              <tr
                key={i.id}
                className="rowLink"
                onClick={() => setOpen(i)}
              >
                <td data-l="Invoice"><b>{i.number}</b></td>
                <td data-l="Period">
                  {i.period_start
                    ? `${day(i.period_start)} to ${day(i.period_end)}`
                    : 'one off'}
                </td>
                <td data-l="Issued">{day(i.issued_at)}</td>
                <td data-l="Status">
                  <span className={`pill ${STATE[i.status] ?? 'warn'}`}>
                    {i.paid_at ? `paid ${day(i.paid_at)}` : i.status}
                  </span>
                </td>
                <td data-l="Amount" className="r">
                  {money(i.amount_cents, i.currency ?? 'USD')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open && (
        <Modal
          title={`Invoice ${open.number}`}
          subtitle={open.paid_at
            ? `Paid ${day(open.paid_at)}`
            : `${open.status} \u00b7 ${money(open.amount_cents,
                open.currency ?? 'USD')}`}
          onClose={() => setOpen(null)}
          onPrint={() => doPrint(open.id)}
        >
          <dl className="defList">
            <dt>Invoice</dt><dd>{open.number}</dd>
            <dt>Issued</dt><dd>{day(open.issued_at)}</dd>
            {open.due_at && (
              <><dt>Due</dt><dd>{day(open.due_at)}</dd></>
            )}
            <dt>Status</dt><dd>{open.status}</dd>
            <dt>Amount</dt>
            <dd>{money(open.amount_cents, open.currency ?? 'USD')}</dd>
          </dl>

          {open.status !== 'paid' && payTo && (
            <div className="payPanel">
              <b>How to pay</b>
              <p>{payTo}</p>
              <span className="sub">
                Quote invoice {open.number} so the payment can be
                matched to it.
              </span>
            </div>
          )}
        </Modal>
      )}
    </>
  );
};
